import { useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "./lib/http/axiosConfig";
import Header from "./Components/common/Header";
import "./Styles/3_Dronepage.css";
import DroneMap from "./Components/Drone/DroneMap";
import DroneStreamViewer from "./Components/Drone/DroneStreamViewer";
import useMarkers from "./Components/Drone/useMarkers";
import Sidebar from "./Components/Sidebar/Sidebar";
import CellSelectionBar from "./Components/Sidebar/CellSelectionBar";
import FarmOverlay from "./Components/Sidebar/FarmOverlay";
import FlightNote from "./Components/Sidebar/FlightNote";
import StreamFloat from "./Components/Drone/StreamFloat";
import DemoStreamFloat from "./Components/Drone/DemoStreamFloat";
import "./Components/Sidebar/Missiontab/Mission.css";
import { useDispatch, useSelector } from "react-redux";
import {
  editWaypoint,
  removeWaypoint,
  setRouteId,
  syncSelectedCellIds,
  setWaypoints,
} from "./features/RouteSlice.js";
import { useFarmCellSelection } from "./hooks/useFarmCellSelection";
import { setBoundary, setFarmId, setGridCells } from "./features/FarmSlice";
import { toast } from "react-toastify";
import { useDroneSocket } from "./hooks/useDroneSocket";
import { useDemoScenario } from "./demo/DemoScenarioContext";
import { useT } from "./i18n";

import DroneMapTest from "./Components/Drone/DroneMapTest";

const parseFarmIdParam = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (!Number.isInteger(num) || num <= 0) return null;
  return num;
};

const isProductionRuntime = () => process.env.NODE_ENV === "production";

export const isEmbedMode = (search = "") => {
  const params = new URLSearchParams(search || "");
  return params.get("embed") === "1";
};

function Smartdrone() {
  const mapRef = useRef(null);
  const { isDemo, demoStream } = useDemoScenario();
  useDroneSocket();
  const location = useLocation();
  const t = useT();

  const dispatch = useDispatch();
  const waypoints = useSelector((state) => state.route.waypoints);
  const routeId = useSelector((state) => state.route.routeId);
  const farmId = useSelector((state) => state.farm.farmId);
  const isNDVI = useSelector((state) => state.tab.toggle);
  const missionStatus = useSelector(
    (state) => state.drone.control.missionStatus
  );
  const droneData = useSelector((state) => state.drone.droneData);
  const gridCellsRef = useRef([]);
  const gridCenterMapRef = useRef(new Map());
  const waypointDataRef = useRef(new Map());
  const handleMarkerRightClickRef = useRef(null);
  const handleMarkerDragEndRef = useRef(null);
  const selectedCellIds = useSelector((state) => state.route.selectedCellIds);
  const isRouteConfirmed = useSelector((state) => state.route.isRouteConfirmed);

  const embedMode = useMemo(() => isEmbedMode(location.search), [location.search]);
  const embedFarmId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return parseFarmIdParam(params.get("farmId"));
  }, [location.search]);
  const droneSerialNum = useSelector((state) => state.drone.control?.serialNum ?? "");
  useEffect(() => {
    if (!embedMode) return;
    if (!embedFarmId) return;
    if (farmId === embedFarmId) return;
    dispatch(setFarmId(embedFarmId));
  }, [embedMode, embedFarmId, farmId, dispatch]);

  useEffect(() => {
    const fetchMapData = async () => {
      if (embedMode) return;
      if (!farmId) return;

      try {
        const { data } = await axios.get(`/api/farms/${farmId}/map-overlay`);
        const payload = data.data;
        console.log("map overlay: ", payload);

        dispatch(setBoundary(payload.boundary));
        const gridCells = payload.gridCells?.items ?? [];
        const centerMap = new Map(gridCells.map((cell) => [cell.id, cell.center]));
        gridCellsRef.current = gridCells;
        gridCenterMapRef.current = centerMap;
        dispatch(setGridCells(gridCells));
        dispatch(setRouteId(payload.routeId));
      } catch (error) {
        console.log("map overlay load failed: ", error);
      }
    };
    fetchMapData();
  }, [embedMode, farmId, dispatch]);

  // 선택은 이제 농장 단위 공유 값이다. 화면에 들어올 때 지우지 않고 서버의 현재 값을 받아 그린다.
  const { toggleCell } = useFarmCellSelection(embedMode ? null : farmId);

  useEffect(() => {
    if (!selectedCellIds.length) {
      waypointDataRef.current = new Map();
      return;
    }
    const map = new Map();
    waypoints.forEach((wp) => {
      map.set(wp.gridCellId ?? wp.id, { altitude: wp.altitude, seq: wp.seq });
    });
    waypointDataRef.current = map;
  }, [waypoints, selectedCellIds]);

  useEffect(() => {
    if (embedMode) return;
    if (!selectedCellIds.length) {
      dispatch(setWaypoints([]));
      return;
    }

    const centerMap = gridCenterMapRef.current;
    const existingData = waypointDataRef.current;
    const selected = selectedCellIds
      .map((id) => ({ id, center: centerMap.get(id) }))
      .filter((cell) => cell.center);

    if (!selected.length) {
      dispatch(setWaypoints([]));
      return;
    }

    const waypointsFromCells = selected.map((cell, idx) => {
      const prev = existingData.get(cell.id);
      return {
        id: cell.id,
        seq: prev?.seq ?? idx + 1,
        gridCellId: cell.id,
        latitude: cell.center.latitude,
        longitude: cell.center.longitude,
        altitude: prev?.altitude ?? 0,
      };
    });

    waypointsFromCells.sort((a, b) => a.seq - b.seq);
    waypointsFromCells.forEach((wp, idx) => { wp.seq = idx + 1; });

    dispatch(setWaypoints(waypointsFromCells));
  }, [embedMode, selectedCellIds, dispatch]);

  const findNearestGridCell = useCallback((lat, lng) => {
    const items = gridCellsRef.current;
    if (!items.length) return null;

    let best = null;
    let bestDist = Infinity;

    items.forEach((cell) => {
      const center = cell.center;
      if (!center) return;
      const dLat = center.latitude - lat;
      const dLng = center.longitude - lng;
      const dist = dLat * dLat + dLng * dLng;
      if (dist < bestDist) {
        bestDist = dist;
        best = { id: cell.id, center };
      }
    });

    return best;
  }, []);

  const handleMapClick = useCallback(
    (latLng) => {
      if (isNDVI) return;
      if (!latLng) return;
    },
    [isNDVI]
  );

  const onMarkerRightClick = useCallback((id) => {
    handleMarkerRightClickRef.current?.(id);
  }, []);

  const onMarkerDragEnd = useCallback((payload) => {
    handleMarkerDragEndRef.current?.(payload);
  }, []);

  const {
    markersRef,
    addMarker,
    removeMarker,
    setMarkerPosition,
    resetMap,
    clearMarkers,
    updatePolyline,
    setProgressIndex,
    revertMarkerPosition,
  } = useMarkers(mapRef, null, onMarkerRightClick, onMarkerDragEnd);

  const handleMarkerRightClick = useCallback(
    (id) => {
      if (missionStatus) {
        toast.warn(t("common.dronePage.routeEditLocked"));
        return;
      }
      if (selectedCellIds.includes(id)) {
        dispatch(
          syncSelectedCellIds(
            selectedCellIds.filter((cellId) => cellId !== id)
          )
        );
        return;
      }
      if (isNDVI) return;
      removeMarker(id);

      if (!routeId) return;

      dispatch(removeWaypoint({ routeId, id }));
    },
    [dispatch, routeId, isNDVI, removeMarker, selectedCellIds, missionStatus, t]
  );

  const handleMarkerDragEnd = useCallback(
    ({ id, lat, lng, alt }) => {
      if (isNDVI) return;
      if (missionStatus) {
        toast.warn(t("common.dronePage.routeEditLocked"));
        return;
      }
      if (!routeId) return;
      if (selectedCellIds.includes(id)) return;

      const nearest = findNearestGridCell(lat, lng);
      if (!nearest) return;
      setMarkerPosition(id, nearest.center.latitude, nearest.center.longitude);
      const editWp = { gridCellId: nearest.id, altitude: alt };

      dispatch(editWaypoint({ routeId, id, editWp }))
        .unwrap()
        .catch(() => revertMarkerPosition(id));
    },
    [
      dispatch,
      routeId,
      isNDVI,
      missionStatus,
      selectedCellIds,
      findNearestGridCell,
      setMarkerPosition,
      revertMarkerPosition,
      t,
    ]
  );

  useEffect(() => {
    handleMarkerRightClickRef.current = handleMarkerRightClick;
    handleMarkerDragEndRef.current = handleMarkerDragEnd;
  }, [handleMarkerRightClick, handleMarkerDragEnd]);

  const handleCellClick = useCallback(
    (cell) => {
      if (!cell?.id || isNDVI) return;
      if (missionStatus) {
        toast.warn(t("common.dronePage.routeEditLocked"));
        return;
      }
      const isSelected = selectedCellIds.includes(cell.id);
      const next = isSelected
        ? selectedCellIds.filter((id) => id !== cell.id)
        : [...selectedCellIds, cell.id];
      toggleCell(cell.id, !isSelected, next);
    },
    [toggleCell, isNDVI, missionStatus, selectedCellIds, t]
  );

  const renderWaypoints = useCallback(() => {
    if (!mapRef.current) return;
    if (!waypoints.length || (!missionStatus && !isRouteConfirmed)) {
      clearMarkers(false);
      setProgressIndex(-1);
      return;
    }

    clearMarkers(false);
    // 웨이포인트 핀/번호는 지도에 표시하지 않는다(데모 포함). 경로선만 남긴다.
    waypoints.forEach((wp) => {
      const center = gridCenterMapRef.current.get(wp.gridCellId);
      const lat = parseFloat(wp.latitude ?? center?.latitude);
      const lng = parseFloat(wp.longitude ?? center?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      addMarker({ lat, lng }, wp.altitude ?? 0, false, wp.id, wp.seq, false, false, true);
    });
    updatePolyline();
  }, [
    mapRef,
    waypoints,
    clearMarkers,
    addMarker,
    updatePolyline,
    missionStatus,
    isRouteConfirmed,
    setProgressIndex,
  ]);

  const findNearestWaypointIndex = useCallback(
    (lat, lng) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return -1;
      if (!waypoints.length) return -1;

      let bestIdx = -1;
      let bestDist = Infinity;
      waypoints.forEach((wp, idx) => {
        const wLat = Number(wp.latitude);
        const wLng = Number(wp.longitude);
        if (!Number.isFinite(wLat) || !Number.isFinite(wLng)) return;
        const dLat = wLat - lat;
        const dLng = wLng - lng;
        const dist = dLat * dLat + dLng * dLng;
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = idx;
        }
      });

      return bestIdx;
    },
    [waypoints]
  );

  useEffect(() => {
    if (embedMode) return;
    if (!missionStatus) {
      setProgressIndex(-1);
      return;
    }
    if (!droneData) return;

    const completedCount = Number(droneData.waypoint_done);
    const liveRouteId = Number(droneData.route_id);
    const selectedRouteId = Number(routeId);
    const routeMatches =
      !Number.isFinite(liveRouteId) ||
      !Number.isFinite(selectedRouteId) ||
      liveRouteId === selectedRouteId;

    if (Number.isFinite(completedCount) && completedCount >= 0 && routeMatches) {
      const completedWaypointCount = Math.min(
        Math.trunc(completedCount),
        waypoints.length
      );
      setProgressIndex(completedWaypointCount - 1);
      return;
    }

    const lat = Number(droneData.latitude);
    const lng = Number(droneData.longitude);
    const idx = findNearestWaypointIndex(lat, lng);
    setProgressIndex(idx);
  }, [
    embedMode,
    missionStatus,
    droneData,
    routeId,
    waypoints.length,
    findNearestWaypointIndex,
    setProgressIndex,
  ]);

  const handleNDVIMode = useCallback(() => {
    if (embedMode) return;
    if (!mapRef.current) return;

    if (isNDVI) {
      markersRef.current.forEach((m) => m.setMap(null));
      resetMap();
    } else {
      renderWaypoints();
    }
  }, [embedMode, isNDVI, mapRef, markersRef, resetMap, renderWaypoints]);

  useEffect(() => {
    handleNDVIMode();
  }, [handleNDVIMode]);

  useEffect(() => {
    if (embedMode) return;
    if (isNDVI) return;
    renderWaypoints();
  }, [embedMode, isNDVI, renderWaypoints, waypoints.length, missionStatus]);

  if (embedMode) {
    return (
      <main className="drone-embed-shell">
        <div className="drone-embed-stage">
          {!embedFarmId && (
            <div className="drone-embed-notice">
              {t("common.dronePage.embedMissingFarmId")}
            </div>
          )}
          <DroneStreamViewer
            serialNum={droneSerialNum}
            title={t("common.dronePage.embedStreamTitle")}
          />
        </div>
      </main>
    );
  }

  return (
    <>
      <Header />
      <main className="drone-main main">
        <div className="map-stage">
          <div className="map-container">
            {/* <DroneMap
              mapRef={mapRef}
              onMapClick={handleMapClick}
              onCellClick={handleCellClick}
              selectedCellIds={selectedCellIds}
            /> */}
            <DroneMapTest
              mapRef={mapRef}
              onMapClick={handleMapClick}
              onCellClick={handleCellClick}
              selectedCellIds={selectedCellIds}
            />
          </div>
          <FlightNote />
          <CellSelectionBar />
          <FarmOverlay />
          {/* 실제 계정은 기체 송출을, 데모는 준비된 mp4 시연 영상을 쓴다(demo2 는 스트리밍 없음).
              데모 영상은 미션의 flying / processing / returning 단계에서만 표시된다. */}
          {!isDemo && <StreamFloat />}
          {isDemo && demoStream && <DemoStreamFloat />}
          <Sidebar />
        </div>
      </main>
    </>
  );
}

export default Smartdrone;
